import { Component, ReactNode, ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw, Bug, Home, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/catalyst/button';
import { Heading } from '@/components/catalyst/heading';
import { Text } from '@/components/catalyst/text';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, resetError: () => void) => ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorCount: number;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError } = this.props;

    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('Error caught by boundary:', error, errorInfo);
    }

    // Call optional error handler
    if (onError) {
      onError(error, errorInfo);
    }

    // Update state with error info
    this.setState((prevState) => ({
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));

    // Report to error tracking service (e.g., Sentry) in production
    if (import.meta.env.PROD) {
      // TODO: Integrate with error tracking service
      console.error('Production error:', {
        error: error.toString(),
        stack: error.stack,
        componentStack: errorInfo.componentStack,
      });
    }
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    const { hasError, error, errorCount } = this.state;
    const { children, fallback } = this.props;

    if (hasError && error) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback(error, this.resetError);
      }

      // Determine error type
      const is404 = error.message?.includes('404') || error.message?.includes('not found');
      const isNetworkError = error.message?.includes('network') || error.message?.includes('fetch');
      const isChunkError = error.message?.includes('chunk') || error.message?.includes('module');

      return (
        <div className="flex min-h-screen items-center justify-center bg-white p-4 dark:bg-zinc-900">
          <div className="w-full max-w-md">
            <div className="text-center">
              {/* Error Icon */}
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
                {is404 ? (
                  <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
                ) : isNetworkError ? (
                  <AlertTriangle className="h-8 w-8 text-orange-600 dark:text-orange-400" />
                ) : (
                  <Bug className="h-8 w-8 text-red-600 dark:text-red-400" />
                )}
              </div>

              {/* Error Title */}
              <Heading level={1} className="mb-2">
                {is404
                  ? 'Page Not Found'
                  : isNetworkError
                    ? 'Connection Error'
                    : 'Something went wrong'}
              </Heading>

              {/* Error Message */}
              <Text className="mb-8 text-zinc-600 dark:text-zinc-400">
                {is404
                  ? "The page you're looking for doesn't exist or has been moved."
                  : isNetworkError
                    ? 'Unable to connect to our servers. Please check your internet connection and try again.'
                    : isChunkError
                      ? 'The application needs to be refreshed to load the latest version.'
                      : 'An unexpected error occurred. We apologize for the inconvenience.'}
              </Text>

              {/* Error Details (Development Only) */}
              {import.meta.env.DEV && (
                <div className="mb-8 rounded-lg bg-zinc-100 p-4 text-left dark:bg-zinc-800">
                  <Text className="font-mono text-xs break-all text-zinc-700 dark:text-zinc-300">
                    {error.toString()}
                  </Text>
                  {error.stack && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-xs text-zinc-500 dark:text-zinc-400">
                        Stack trace
                      </summary>
                      <pre className="mt-2 overflow-auto text-xs text-zinc-600 dark:text-zinc-400">
                        {error.stack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              {/* Retry Count Warning */}
              {errorCount > 2 && (
                <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
                  <Text className="text-sm text-amber-800 dark:text-amber-200">
                    This error has occurred multiple times. You may need to clear your browser cache
                    or contact support.
                  </Text>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col justify-center gap-3 sm:flex-row">
                {isChunkError ? (
                  <Button
                    onClick={() => window.location.reload()}
                    className="inline-flex items-center"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Reload Application
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={this.resetError}
                      color="blue"
                      className="inline-flex items-center"
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Try Again
                    </Button>
                    <Button
                      onClick={() => window.history.back()}
                      outline
                      className="inline-flex items-center"
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Go Back
                    </Button>
                    <Button
                      onClick={() => (window.location.href = '/')}
                      outline
                      className="inline-flex items-center"
                    >
                      <Home className="mr-2 h-4 w-4" />
                      Home
                    </Button>
                  </>
                )}
              </div>

              {/* Support Link */}
              {import.meta.env.PROD && (
                <div className="mt-8">
                  <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                    If this problem persists,{' '}
                    <a
                      href="mailto:support@sendguard.io"
                      className="text-blue-600 hover:underline dark:text-blue-400"
                    >
                      contact support
                    </a>{' '}
                    with error code: {Date.now()}
                  </Text>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}
